// Phase 36 Batch AM — Saved search builder modal.
//
// Single form used both for **create** (when `initial` is null) and
// **edit** (when `initial` is a saved-search doc). On submit calls the
// `onSave({ name, filters, email_digest, in_app_only })` handler; the
// parent owns the actual POST/PATCH call so this stays pure.
//
// Mobile: shadcn Dialog is full-screen on viewports < 640px out of the
// box (max-w-lg responsive container). Inputs stack to one column at
// that width.

import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const PROPERTY_TYPES = [
  { value: "", label: "Any type" },
  { value: "single family", label: "Single Family" },
  { value: "condo", label: "Condo" },
  { value: "townhouse", label: "Townhouse" },
  { value: "multi family", label: "Multifamily" },
];

const DAYS_WINDOW = [
  { value: "0", label: "Any time" },
  { value: "7", label: "Past 7 days" },
  { value: "30", label: "Past 30 days" },
  { value: "90", label: "Past 90 days" },
];

export default function SavedSearchModal({
  open,
  onOpenChange,
  initial = null,
  onSave,
}) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [zip, setZip] = useState("");
  const [propertyType, setPropertyType] = useState("");
  const [bedsMin, setBedsMin] = useState("");
  const [bathsMin, setBathsMin] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [minCap, setMinCap] = useState("");
  const [minCashFlow, setMinCashFlow] = useState("");
  const [daysOld, setDaysOld] = useState("0");
  const [emailDigest, setEmailDigest] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  // Hydrate from `initial` whenever the modal opens with an edit
  // payload. Without this, second-time edits would carry stale state
  // from a prior open.
  useEffect(() => {
    if (!open) return;
    const f = initial?.filters || {};
    setName(initial?.name || "");
    setCity(f.city || "");
    setState((f.state || "").toUpperCase());
    setZip(f.zip_code || "");
    setPropertyType(f.property_type || "");
    setBedsMin(f.bedrooms_min ?? "");
    setBathsMin(f.bathrooms_min ?? "");
    setMaxPrice(f.max_price ?? "");
    setMinCap(f.min_cap_rate_pct ?? "");
    setMinCashFlow(f.min_cash_flow_monthly ?? "");
    setDaysOld(String(f.days_old ?? 0));
    setEmailDigest(initial?.email_digest ?? true);
    setErr("");
  }, [open, initial]);

  const _num = (v) => {
    if (v === "" || v === null || v === undefined) return undefined;
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      setErr("Search name is required");
      return;
    }
    if (!city.trim() && !zip.trim()) {
      setErr("Provide at least a city or a ZIP code");
      return;
    }
    setSubmitting(true);
    setErr("");
    const filters = {
      city: city.trim() || undefined,
      state: state.trim() || undefined,
      zip_code: zip.trim() || undefined,
      property_type:
        propertyType && propertyType !== "__any" ? propertyType : undefined,
      bedrooms_min: _num(bedsMin),
      bathrooms_min: _num(bathsMin),
      max_price: _num(maxPrice),
      min_cap_rate_pct: _num(minCap),
      min_cash_flow_monthly: _num(minCashFlow),
      days_old: daysOld === "0" ? undefined : _num(daysOld),
    };
    // Strip undefined keys so the backend extra=ignore stays predictable.
    Object.keys(filters).forEach(
      (k) => filters[k] === undefined && delete filters[k],
    );
    try {
      await onSave?.({
        name: name.trim(),
        filters,
        email_digest: emailDigest,
        in_app_only: false,
      });
      onOpenChange?.(false);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2?.message || "Could not save search");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[90vh] overflow-y-auto sm:max-w-lg"
        data-testid="saved-search-modal"
      >
        <DialogHeader>
          <DialogTitle data-testid="saved-search-modal-title">
            {isEdit ? "Edit saved search" : "Create saved search"}
          </DialogTitle>
          <DialogDescription>
            We&rsquo;ll alert you by email whenever new listings match these
            filters.
          </DialogDescription>
        </DialogHeader>

        <form className="space-y-4" onSubmit={handleSubmit}>
          <div>
            <Label htmlFor="ss-name">Search name</Label>
            <Input
              id="ss-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Miami SFR under 300k"
              data-testid="ss-name-input"
              autoFocus
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="ss-city">City</Label>
              <Input
                id="ss-city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                placeholder="Miami"
                data-testid="ss-city-input"
              />
            </div>
            <div>
              <Label htmlFor="ss-state">State (2-letter)</Label>
              <Input
                id="ss-state"
                value={state}
                onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
                placeholder="FL"
                maxLength={2}
                data-testid="ss-state-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ss-zip">ZIP code (optional)</Label>
            <Input
              id="ss-zip"
              value={zip}
              onChange={(e) => setZip(e.target.value)}
              placeholder="33101"
              data-testid="ss-zip-input"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="ss-type">Property type</Label>
              <Select value={propertyType} onValueChange={setPropertyType}>
                <SelectTrigger id="ss-type" data-testid="ss-type-select">
                  <SelectValue placeholder="Any type" />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_TYPES.map((p) => (
                    <SelectItem key={p.value || "any"} value={p.value || "__any"}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="ss-days">Listing window</Label>
              <Select value={daysOld} onValueChange={setDaysOld}>
                <SelectTrigger id="ss-days" data-testid="ss-days-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {DAYS_WINDOW.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ss-beds">Min beds</Label>
              <Input
                id="ss-beds"
                type="number"
                min="0"
                max="20"
                value={bedsMin}
                onChange={(e) => setBedsMin(e.target.value)}
                placeholder="3"
                data-testid="ss-beds-input"
              />
            </div>
            <div>
              <Label htmlFor="ss-baths">Min baths</Label>
              <Input
                id="ss-baths"
                type="number"
                min="0"
                max="20"
                step="0.5"
                value={bathsMin}
                onChange={(e) => setBathsMin(e.target.value)}
                placeholder="2"
                data-testid="ss-baths-input"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="ss-price">Max price ($)</Label>
            <Input
              id="ss-price"
              type="number"
              min="0"
              value={maxPrice}
              onChange={(e) => setMaxPrice(e.target.value)}
              placeholder="300000"
              data-testid="ss-price-input"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="ss-cap">Min cap rate (%)</Label>
              <Input
                id="ss-cap"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={minCap}
                onChange={(e) => setMinCap(e.target.value)}
                placeholder="6"
                data-testid="ss-cap-input"
              />
            </div>
            <div>
              <Label htmlFor="ss-cf">Min cash flow ($/mo)</Label>
              <Input
                id="ss-cf"
                type="number"
                value={minCashFlow}
                onChange={(e) => setMinCashFlow(e.target.value)}
                placeholder="200"
                data-testid="ss-cf-input"
              />
            </div>
          </div>

          <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
            <div className="text-xs text-slate-600">
              <p className="font-medium text-slate-800">Daily email digest</p>
              <p>14:00 UTC — new matches only.</p>
            </div>
            <Switch
              checked={emailDigest}
              onCheckedChange={setEmailDigest}
              data-testid="ss-email-digest-toggle"
              aria-label="Toggle daily email digest"
            />
          </div>

          {err && (
            <p
              data-testid="saved-search-modal-error"
              className="text-xs font-medium text-rose-600"
            >
              {err}
            </p>
          )}

          <DialogFooter className="flex-col gap-2 sm:flex-row sm:gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange?.(false)}
              data-testid="saved-search-modal-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="saved-search-modal-save"
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create search"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
